export interface DevicePosition {
  latitude: number;
  longitude: number;
  accuracyMetres: number;
}

export class GeolocationUnavailableError extends Error {
  constructor() {
    super('GEOLOCATION_UNAVAILABLE');
    this.name = 'GeolocationUnavailableError';
  }
}

export class GeolocationDeniedError extends Error {
  constructor() {
    super('GEOLOCATION_DENIED');
    this.name = 'GeolocationDeniedError';
  }
}

export type GeolocationPermissionState = 'granted' | 'prompt' | 'denied' | 'unsupported';

const locationAckKey = (pairingCode: string) => `trinity-kiosk-location-ack:${pairingCode}`;

/** User already allowed location for this kiosk on this device (survives WebView restarts). */
export const hasLocationConsentAck = (pairingCode: string): boolean =>
  localStorage.getItem(locationAckKey(pairingCode)) === '1';

export const writeLocationConsentAck = (pairingCode: string): void => {
  localStorage.setItem(locationAckKey(pairingCode), '1');
};

/** Whether the browser will show a permission prompt, or location is already allowed. */
export const queryGeolocationPermission = async (): Promise<GeolocationPermissionState> => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return 'unsupported';
  }

  if (!navigator.permissions) {
    return 'prompt';
  }

  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    if (result.state === 'granted' || result.state === 'prompt' || result.state === 'denied') {
      return result.state;
    }
    return 'prompt';
  } catch {
    return 'prompt';
  }
};

/** Fresh device position — `maximumAge: 0` so each poll re-reads GPS. */
export const readDevicePosition = (): Promise<DevicePosition> =>
  new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new GeolocationUnavailableError());
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMetres: position.coords.accuracy,
        });
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          reject(new GeolocationDeniedError());
          return;
        }
        reject(new GeolocationUnavailableError());
      },
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 },
    );
  });

export const positionQueryString = (position: DevicePosition): string => {
  const params = new URLSearchParams({
    latitude: String(position.latitude),
    longitude: String(position.longitude),
    accuracyMetres: String(position.accuracyMetres),
  });
  return params.toString();
};
